import React, { useState, useEffect } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { useRecommendations } from '@/hooks/useRecommendations';
import { useSavedRecommendations } from '@/hooks/useSavedRecommendations';
import { useUserData, defaultUserData } from '@/context/UserDataContext';
import SupplementItem from '../components/SupplementItem';
import SupplementDetails from '../components/SupplementDetails';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import PageTransition from '@/components/PageTransition';
import { ChevronLeft, AlertTriangle, Sparkles, Zap, Leaf, RefreshCw, Edit, LayoutGrid, HelpCircle, Star, Copy, Save } from 'lucide-react';
import { toast } from 'sonner';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { RecommendationProps } from '@/components/RecommendationCard';
import { cn } from '@/lib/utils';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { saveResults } from '@/integrations/supabase/savedResults';
import { HEALTH_CONCERNS } from '@/components/form/shared/constants';
import { usePageTitle } from '@/hooks/usePageTitle';

const ResultsFullPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { userData, isDataComplete, setUserData } = useUserData();
  const [selectedSupplement, setSelectedSupplement] = useState<RecommendationProps | null>(null);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [showHowDialog, setShowHowDialog] = useState(false);
  const [isDesktop, setIsDesktop] = useState(window.innerWidth >= 1024);
  const [email, setEmail] = useState('');
  const [emailError, setEmailError] = useState('');
  const [hardlink, setHardlink] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [clickedRecommendations, setClickedRecommendations] = useState<Set<string>>(new Set());
  const [hasShownEmailModal, setHasShownEmailModal] = useState(false);
  const [hasDismissedEmailModal, setHasDismissedEmailModal] = useState(false);
  const [hasSubmittedEmail, setHasSubmittedEmail] = useState(false);

  usePageTitle('Your Recommendations');

  // Check if we're in the saved results flow by looking at the URL
  const isSavedResultsFlow = React.useMemo(() => {
    return location.pathname.startsWith('/results/') && location.pathname !== '/results';
  }, [location.pathname]);

  // Use the appropriate hook based on whether we're in the saved results flow
  const { recommendations, isLoading, error } = isSavedResultsFlow 
    ? useSavedRecommendations() 
    : useRecommendations();
  
  // Sort recommendations by priority
  const sortedRecommendations = React.useMemo(() => {
    if (!recommendations || recommendations.length === 0) return [];
    
    // Sort by priority: high -> medium -> low
    return [...recommendations].sort((a, b) => {
      const priorityOrder = { high: 0, medium: 1, low: 2 };
      return priorityOrder[a.priority] - priorityOrder[b.priority];
    });
  }, [recommendations]);

  // Add initial selection for desktop
  useEffect(() => {
    // Check if we're on desktop and have recommendations but no selection
    if (window.innerWidth >= 1024 && sortedRecommendations.length > 0 && !selectedSupplement) {
      setSelectedSupplement(sortedRecommendations[0]);
    }
  }, [sortedRecommendations]); // Only run when recommendations load

  // Add window resize listener
  useEffect(() => {
    const handleResize = () => {
      const isDesktop = window.innerWidth >= 1024;
      if (!isDesktop) {
        setSelectedSupplement(null);
      } else if (sortedRecommendations.length > 0 && !selectedSupplement) {
        setSelectedSupplement(sortedRecommendations[0]);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [sortedRecommendations, selectedSupplement]);

  // Add window resize listener for desktop check
  useEffect(() => {
    const handleResize = () => {
      setIsDesktop(window.innerWidth >= 1024);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (!isDataComplete) {
      navigate('/');
    }
  }, [isDataComplete, navigate]);

  const handleBackClick = () => {
    setUserData(defaultUserData);
    navigate('/');
  };

  const handleRetry = () => {
    window.location.reload();
  };

  const handleSelectSupplement = (supplement: RecommendationProps) => {
    setSelectedSupplement(supplement);
    
    // Add the clicked recommendation to the set
    setClickedRecommendations(prev => {
      const newSet = new Set(prev);
      newSet.add(supplement.id);
      return newSet;
    });

    // Show email modal if we've clicked 3 unique recommendations and haven't shown it yet
    if (clickedRecommendations.size === 2 && !hasShownEmailModal) {
      setShowEmailModal(true);
      setHasShownEmailModal(true);
    }
  };

  const handleEditResponses = () => {
    // Set the current step to the last question (lifestyle)
    setUserData(prev => ({ 
      ...prev, 
      currentStep: 'lifestyle',
      lastQuestion: 'smoking', // Set the last question to smoking
      isBasicFormOnly: false // Ensure we're not in basic form mode
    }));
    
    // Use setTimeout to ensure state is updated before navigation
    setTimeout(() => {
      navigate('/questionnaire');
    }, 0);
  };

  const validateEmail = (email: string) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newEmail = e.target.value;
    setEmail(newEmail);
    if (newEmail && !validateEmail(newEmail)) {
      setEmailError('Please enter a valid email address');
    } else {
      setEmailError('');
    }
  };

  const handleEmailSubmit = async () => {
    if (!validateEmail(email)) {
      setEmailError('Please enter a valid email address');
      return;
    }

    setIsSaving(true);
    try {
      // Prepare the data to save - only include user-specific data and supplement IDs
      const dataToSave = {
        userData: {
          age: userData.age,
          gender: userData.gender,
          goals: userData.goals,
          healthConcerns: userData.healthConcerns,
          currentSupplements: userData.currentSupplements,
          exerciseFrequency: userData.exerciseFrequency,
          exerciseType: userData.exerciseType,
          stressLevel: userData.stressLevel,
          alcoholConsumption: userData.alcoholConsumption,
          smoking: userData.smoking,
          meatConsumption: userData.meatConsumption,
          seafoodConsumption: userData.seafoodConsumption,
          dairyConsumption: userData.dairyConsumption
        },
        recommendationIds: sortedRecommendations.map(rec => ({
          id: rec.supplementId,
          priority: rec.priority,
          reason: rec.reason,
          notes: rec.notes
        }))
      };

      const result = await saveResults(email, dataToSave);

      if (result.success) {
        setHasSubmittedEmail(true);
        // Extract just the hardlink ID from the full URL
        const hardlinkId = result.hardlink.split('/').pop() || '';
        setHardlink(result.hardlink);
        
        // Update the URL without causing a reload
        window.history.replaceState(null, '', `/results/${hardlinkId}`);
        
        setShowEmailModal(false);
        setShowConfirmationModal(true);
      } else {
        toast.error('Failed to save your results. Please try again.');
      }
    } catch (error) {
      console.error('Error saving results:', error);
      toast.error('An unexpected error occurred. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  // Check if there are no recommendations after loading
  const hasNoRecommendations = !isLoading && (!recommendations || recommendations.length === 0);

  // Add a helper function to format the list with "and" before the last item
  const formatGoalsList = (goals: Array<{value: string}>, options?: { usePlural?: boolean }) => {
    if (!goals || goals.length === 0) return '';
    
    const formattedGoals = goals.map(goal => goal.value.replace('_', ' '));
    
    // Handle the plurality of "goal" vs "goals" if the option is specified
    const prefix = options?.usePlural !== undefined 
      ? (goals.length === 1 ? 'goal' : 'goals') 
      : '';
    
    if (formattedGoals.length === 1) {
      return prefix ? `${prefix} to improve ${formattedGoals[0]}` : formattedGoals[0];
    }
    
    const joinedGoals = formattedGoals.slice(0, -1).join(', ') + ' and ' + formattedGoals[formattedGoals.length - 1];
    return prefix ? `${prefix} to improve ${joinedGoals}` : joinedGoals;
  };

  // Add helper function to get health concern label
  const getHealthConcernLabel = (concernId: string): string => {
    for (const category of HEALTH_CONCERNS) {
      const item = category.items.find(item => item.id === concernId);
      if (item) return item.label;
    }
    return concernId; // Fallback to ID if label not found
  };

  if (!isDataComplete) {
    return null;
  }

  // Add full page loading state
  if (isLoading) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-background">
        <div className="text-center space-y-6 -mt-20">
          <div className="relative">
            <svg
              className="animate-spin h-12 w-12 text-primary mx-auto"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
              />
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              />
            </svg>

          </div>
          <div>
            <h2 className="text-xl font-semibold text-gray-900 flex items-center justify-center">
              Generating Your Recommendations
              <span className="inline-flex w-[16px] overflow-hidden">
                <span className="animate-ellipsis overflow-hidden whitespace-nowrap">...</span>
              </span>
            </h2>
            <p className="text-gray-600 max-w-md mt-2">
              Give us a moment while we analyse your profile!
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <PageTransition>
      <Alert variant="destructive" className="hidden lg:block w-full rounded-none py-2 bg-amber-50 border-amber-300 text-amber-800 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto flex items-center justify-center">
          <AlertTriangle className="h-2 w-2 mr-1" />
          <AlertDescription className="text-xs">
            If you have existing health concerns, please consult a doctor. This website is not intended to be used to diagnose or provide treatments for specific medical conditions.
          </AlertDescription>
        </div>
      </Alert>
      
      {/* Save Results Button - Only shown after dismissing without submitting */}
      {hasDismissedEmailModal && !hasSubmittedEmail && (isDesktop || !selectedSupplement) && (
        <Button 
          onClick={() => setShowEmailModal(true)}
          variant="outline"
          className="fixed bottom-4 left-4 z-50 bg-white/30 hover:border-primary/70 backdrop-blur-sm border-gray-200 text-gray-600 hover:bg-white hover:text-gray-900 shadow-sm"
        >
          <Save className="h-4 w-4 mr-2" />
          Save your Results
        </Button>
      )}
      
      {/* Email Collection Modal */}
      <Dialog 
        open={showEmailModal} 
        onOpenChange={(open) => {
          setShowEmailModal(open);
          if (!open) {
            setHasDismissedEmailModal(true);
          }
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle className='mb-4'>Save Your Results?</DialogTitle>
            <DialogDescription>
              Enter your email to receive a link which will allow you to access your personalized supplement recommendations at any time.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={(e) => {
            e.preventDefault();
            if (email && validateEmail(email)) {
              handleEmailSubmit();
            }
          }}>
            <div className="py-4">
              <Input
                type="email"
                placeholder="Enter your email"
                value={email}
                onChange={handleEmailChange}
                disabled={isSaving}
              />
            </div>
            <div className="flex justify-center gap-2">
              <Button 
                variant="outline" 
                type="button" 
                onClick={() => {
                  setShowEmailModal(false);
                  setHasDismissedEmailModal(true);
                }}
                disabled={isSaving}
              >
                Cancel
              </Button>
              <TooltipProvider delayDuration={100}>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span>
                      <Button 
                        type="submit"
                        disabled={!email || !validateEmail(email) || isSaving}
                      >
                        {isSaving ? (
                          <>
                            <span className="mr-2">Saving</span>
                            <svg
                              className="animate-spin h-4 w-4"
                              xmlns="http://www.w3.org/2000/svg"
                              fill="none"
                              viewBox="0 0 24 24"
                            >
                              <circle
                                className="opacity-25"
                                cx="12"
                                cy="12"
                                r="10"
                                stroke="currentColor"
                                strokeWidth="4"
                              />
                              <path
                                className="opacity-75"
                                fill="currentColor"
                                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                              />
                            </svg>
                          </>
                        ) : (
                          'Save Results'
                        )}
                      </Button>
                    </span>
                  </TooltipTrigger>
                  {(!email || !validateEmail(email)) && !isSaving && (
                    <TooltipContent>
                      <p>Please enter a valid email address to continue</p>
                    </TooltipContent>
                  )}
                </Tooltip>
              </TooltipProvider>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Confirmation Modal */}
      <Dialog open={showConfirmationModal} onOpenChange={setShowConfirmationModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className='mb-4'>Results Saved!</DialogTitle>
            <DialogDescription>
              Your personalized supplement recommendations have been saved. You can access them anytime using the link below.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <div 
              className="p-4 bg-muted rounded-md break-all flex items-center justify-between group cursor-pointer hover:bg-muted/80 transition-colors"
              onClick={() => {
                navigator.clipboard.writeText(hardlink);
                toast.success('Link copied to clipboard!', {
                  description: 'You can also bookmark this link to more easily return to your results.'
                });
              }}
            >
              <p className="text-sm font-mono flex-1">{hardlink}</p>
              <Copy className="h-4 w-4 text-muted-foreground group-hover:text-foreground transition-colors" />
            </div>
          </div>

        </DialogContent>
      </Dialog>
      
      <div className="min-h-[calc(100vh-32px)] lg:bg-gradient-to-b lg:from-white lg:to-stone-50">
        
        {/* Background element that extends dynamically */}
        <div className="hidden lg:block fixed left-0 top-[32px] bottom-0 w-screen lg:w-[calc((100vw-1600px)/2+400px+28px)] lg:min-w-[428px] max-w-full bg-gradient-to-r from-background to-background/40"></div>
        
        <div className="mx-auto max-w-[1600px] px-4 sm:px-6 lg:px-8 h-full relative">
          <div className="flex flex-col lg:flex-row h-[calc(100vh-32px)]">
            {/* Left sidebar - fixed height with scrollable content */}
            <div className={cn(
              "lg:w-[400px] lg:pr-6 flex flex-col h-full relative",
              selectedSupplement ? "hidden lg:flex" : "flex"
            )}>
              {/* Content container */}
              <div className="relative z-10 flex flex-col h-full">
                <div className="sticky top-[32px] lg:top-[32px] z-20 pt-6 lg:pt-12 pb-4">
                  <div className="text-center lg:text-left">
                    <h1 className="text-[2.3rem] font-bold font-['Merriweather']">Your Personalized Recommendations</h1>

                    <div className="flex items-center justify-center lg:justify-start gap-2 mt-2">
                      <TooltipProvider>
                        <Tooltip delayDuration={50}>
                          <TooltipTrigger asChild>
                            <Badge 
                              variant="outline" 
                              className="cursor-help flex items-center gap-1 text-xs align-middle"
                              onClick={(e) => {
                                // Only show dialog on mobile
                                if (window.innerWidth < 1024) {
                                  e.preventDefault();
                                  setShowHowDialog(true);
                                }
                              }}
                            >
                              <HelpCircle className="h-3 w-3" />
                              How did we get these?
                            </Badge>
                          </TooltipTrigger>
                          <TooltipContent 
                            side="right" 
                            className="hidden lg:block w-[350px] p-4 z-[100] bg-white/95 backdrop-blur-sm border shadow-lg"
                            sideOffset={5}
                            align="start"
                          >
                            <div className="text-sm space-y-2">
                              <p>These recommendations are based on a combination of: </p>
                              <ul className="list-disc pl-4 space-y-1 mt-2">
                                <li>The average needs of a {userData.age} year old {userData.gender}</li>
                                <li>Your {formatGoalsList(userData.goals, { usePlural: true })}</li>
                                {userData.healthConcerns.length > 0 && (
                                  <li>Your {userData.healthConcerns.length === 1 ? 'health concern' : 'health concerns'}: {userData.healthConcerns.map(getHealthConcernLabel).join(', ')}</li>
                                )}
                                <li>All the other information you provided including your personal circumstances, lifestyle, and health factors</li>
                              </ul>
                            </div>
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>

                      <Badge 
                        variant="outline" 
                        className="cursor-pointer flex items-center gap-1 text-xs hover:bg-gray-100"
                        onClick={handleEditResponses}
                      >
                        <Edit className="h-3 w-3" />
                        Edit Responses
                      </Badge>
                    </div>
                  </div>
                </div>
                
                {/* Scrollable area for recommendation cards */}
                <div className="flex-1 overflow-y-auto scrollbar-none py-2 h-[calc(100vh-200px)] mt-6 lg:mt-8">

                <p className="text-sm text-muted-foreground mt-1 text-center">
                    {sortedRecommendations.length} {sortedRecommendations.length === 1 ? 'recommendation' : 'recommendations'} 
                  </p>

                  {isLoading ? (
                    <div className="space-y-4">
                      {[1, 2, 3, 4, 5].map((i) => (
                        <div key={`skeleton-${i}`} className="border rounded-lg p-4 space-y-2 m-3">
                          <Skeleton className="h-6 w-3/4" />
                          <Skeleton className="h-4 w-full" />
                          <Skeleton className="h-4 w-2/3" />
                        </div>
                      ))}
                    </div>
                  ) : error ? (
                    <div className="p-6">
                      <div className="text-center py-8 border rounded-xl p-6 bg-red-50">
                        <AlertTriangle className="h-10 w-10 text-red-500 mx-auto mb-3" />
                        <p className="text-red-600 font-semibold mb-2">Error Loading Recommendations</p>
                        <p className="text-red-500 mb-4 text-sm">{error}</p>
                        <Button onClick={handleRetry} variant="default" size="sm">
                          <RefreshCw className="mr-2 h-4 w-4" />
                          Try Again
                        </Button>
                      </div>
                    </div>
                  ) : hasNoRecommendations ? (
                    <div className="p-6">
                      <div className="text-center py-8 border rounded-xl p-6 bg-amber-50">
                        <AlertTriangle className="h-10 w-10 text-amber-500 mx-auto mb-3" />
                        <p className="text-amber-600 font-semibold mb-2">No Recommendations Found</p>
                        <p className="text-amber-700 mb-4 text-sm">
                          We couldn't find any supplements matching your profile. This could be due to missing data in our system.
                        </p>
                        <Button onClick={handleEditResponses} variant="outline" size="sm">
                          <Edit className="mr-2 h-4 w-4" />
                          Edit Responses
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <div className="pb-5">
                      {sortedRecommendations.map((supplement) => (
                        <SupplementItem 
                          key={supplement.id}
                          supplement={supplement}
                          isSelected={selectedSupplement?.id === supplement.id}
                          selectedExists={!!selectedSupplement}
                          onClick={() => handleSelectSupplement(supplement)}
                        />
                      ))}
                    </div>
                  )}
                </div>
                

              </div>
            </div>
            
            {/* Vertical divider */}
            <div className="hidden lg:block w-[1px] h-[95%] my-auto bg-gradient-to-b from-[rgba(229,231,235,0.1)] via-gray-300 to-[rgba(229,231,235,0.1)]"></div>
            
            {/* Right content area - independently scrollable */}
            <div className={cn(
              "lg:flex-1 min-h-[calc(100vh-32px)] lg:border-l-0 lg:px-12 overflow-y-auto scrollbar-none relative",
              selectedSupplement ? "flex" : "hidden lg:flex"
            )}>
              {selectedSupplement ? (
                <div className="relative w-full">
                  {/* Mobile Back Button */}
                  <div className="lg:hidden fixed bottom-4 left-4 z-20">
                    <Button 
                      variant="outline" 
                      size="sm" 
                      className="bg-white"
                      onClick={() => setSelectedSupplement(null)}
                    >
                      <ChevronLeft className="h-4 w-4" />
                      Back
                    </Button>
                  </div>
                  <div className="pb-16">
                    <SupplementDetails supplement={selectedSupplement} />
                  </div>
                </div>
              ) : (
                <div className="flex flex-col h-[calc(100vh-200px)] p-6 w-full max-w-[1000px] mx-auto">
                  {/* Hero section skeleton */}
                  <div className="sticky top-0 bg-stone-200/60 py-6 px-6 rounded-xl mb-16 z-10 backdrop-blur-sm w-full">
                    <Skeleton className="h-12 w-[80%] mb-4" />
                  </div>

                  {/* Key Information section skeleton */}
                  <Skeleton className="h-8 w-64 mb-8" />
                  <div className="flex flex-col lg:flex-row lg:flex-wrap bg-white/80 backdrop-blur-sm rounded-lg border shadow-[0px_0px_20px_rgba(0,0,0,0.05)] px-4 w-full">
                    <div className="lg:w-1/2 lg:pr-10 my-6">
                      <div className="flex items-center gap-2 mb-4">
                        <Skeleton className="h-5 w-5" />
                        <Skeleton className="h-6 w-72" />
                      </div>
                      <div className="ml-7">
                        <Skeleton className="h-4 w-full mb-2" />
                        <Skeleton className="h-4 w-[95%] mb-2" />
                        <Skeleton className="h-4 w-[90%]" />
                      </div>
                    </div>
                    <div className="lg:w-1/2 flex flex-col">
                      <div className="my-6">
                        <div className="flex items-center gap-2 mb-4">
                          <Skeleton className="h-5 w-5" />
                          <Skeleton className="h-6 w-72" />
                        </div>
                        <div className="ml-7">
                          <Skeleton className="h-4 w-full mb-2" />
                          <Skeleton className="h-4 w-[95%]" />
                        </div>
                      </div>
                      <div className="my-6">
                        <div className="flex items-center gap-2 mb-4">
                          <Skeleton className="h-5 w-5" />
                          <Skeleton className="h-6 w-72" />
                        </div>
                        <div className="ml-7">
                          <Skeleton className="h-4 w-[85%]" />
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Benefits section skeleton */}
                  <Skeleton className="h-8 w-64 my-12" />
                  <div className="space-y-4 w-full">
                    {[1, 2, 3].map((i) => (
                      <div key={i} className="bg-white/80 backdrop-blur-sm rounded-lg p-4 border shadow-[0px_0px_20px_rgba(0,0,0,0.05)]">
                        <div className="flex items-center gap-2 mb-4">
                          <Skeleton className="h-5 w-5" />
                          <Skeleton className="h-5 w-72" />
                        </div>
                        <Skeleton className="h-4 w-full mb-2" />
                        <Skeleton className="h-4 w-[95%]" />
                      </div>
                    ))}
                  </div>

                  {/* Good to Know section skeleton */}
                  <Skeleton className="h-8 w-64 my-12" />
                  <div className="flex flex-col lg:flex-row lg:flex-wrap bg-white/80 backdrop-blur-sm rounded-lg border shadow-[0px_0px_20px_rgba(0,0,0,0.05)] px-4 mb-10 w-full">
                    {[1, 2].map((i) => (
                      <div key={i} className="lg:w-1/2 my-6 lg:pr-6">
                        <div className="flex items-center gap-2 mb-4">
                          <Skeleton className="h-5 w-5" />
                          <Skeleton className="h-6 w-72" />
                        </div>
                        <div className="ml-7">
                          <Skeleton className="h-4 w-full mb-2" />
                          <Skeleton className="h-4 w-[95%] mb-2" />
                          <Skeleton className="h-4 w-[90%]" />
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Where to buy section skeleton */}
                  <Skeleton className="h-8 w-64 mb-8" />
                  <div className="bg-white/80 backdrop-blur-sm rounded-lg border shadow-[0px_0px_20px_rgba(0,0,0,0.05)] px-4 mb-10 w-full">
                    <div className="my-6">
                      <div className="flex items-center gap-2 mb-4">
                        <Skeleton className="h-5 w-5" />
                        <Skeleton className="h-6 w-72" />
                      </div>
                      <div className="ml-7">
                        <Skeleton className="h-4 w-full mb-2" />
                        <Skeleton className="h-4 w-[95%] mb-2" />
                        <Skeleton className="h-10 w-48 mt-6" />
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </PageTransition>
  );
};

export default ResultsFullPage; 